import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './client'
import { z } from 'zod'
import { previewResponseSchema, runResponseSchema, actionResultSchema, historyResponseSchema, stopResponseSchema, m2cHasResolutionProof, m2cHasPreDispatchProof, m2cHasReconcileResolutionProof, m2cHasNotAppliedProof, m2cHasSupersededProof, m2cHasRejectedProof, m2cHasRestoreNotAppliedProof } from '../components/m2c/m2cSchemas'

/*
 * M2c — lokale DTOs für den T0-Vertrag (backend/src/modules/m2c/schemas.ts).
 * Bewusst KEIN Import aus dem Backend: die UI erhält Grenzen und Zustände nur über HTTP.
 * Runtime-Schemas ergänzen T0 um strengere UI-Beweise; Vertragstests führen beide aus.
 * Die UI hat keine Ausführungsbefugnis: alle Prüfungen hier sind Anzeige/Sperre,
 * die Entscheidung trifft ausschließlich der Server.
 */

export type M2cEntityKind = 'keyword' | 'target'

export interface M2cEntityRef {
  kind: M2cEntityKind
  profileId: number
  campaignId: number
  campaignAmazonId: string
  adGroupId: number
  adGroupAmazonId: string
  entityId: number
  entityAmazonId: string
}

export interface M2cBidAction {
  id: string
  revision: number
  entity: M2cEntityRef
  operation: 'decrease_bid'
  oldBidCents: number
  targetBidCents: number
  permitId: string
  permitRevision: number
  settingsVersion: number
  evidenceRevision: string
  businessKey: string
  firstDispatchedAt: string | null
  recoveryExpiresAt: string | null
}

export interface M2cPilotPermit {
  id: string
  revision: number
  status: 'active' | 'stopped'
  profileId: number
  marketplace: string
  adType: string
  familyKey: string
  asin: string
  campaigns: Array<{ campaignId: number; campaignAmazonId: string }>
  operatorId: number
  limitsVersion: number
  settingsVersion: number
  startsAt: string
  expiresAt: string
  restoreScope: 'action_baseline_owner_approval'
}

export interface M2cLimits {
  limitsVersion: number
  maxCampaigns: number
  targetExecutionEnabled: boolean
  maxReductionPercent: number
  maxReductionCents: number
  minBidCents: number
  maxSourceBidCents: number
  maxDailyChanges: number
  maxDailyCampaignChanges: number
  assessmentDays: number
  maturityBufferDays: number
  minRepeatDays: number
  maxReadbacks: number
  readbackWindowMs: number
  permitDays: number
  recoveryDays: number
  minVolumeRatio: number
  maxSpendRatio: number
  earlyStopVolumeRatio: number
  earlyStopProfitDeltaCents: number
  entitySyncMaxAgeHours: number
  metricsSyncMaxAgeHours: number
  searchTermsSyncMaxAgeHours: number
}

export type M2cReadEvidence =
  | { status: 'found'; entity: M2cEntityRef; bidCents: number; observedAt: string }
  | { status: 'not_found'; entity: M2cEntityRef; observedAt: string }
  | { status: 'identity_mismatch'; requestedEntity: M2cEntityRef; observedEntity: M2cEntityRef; observedAt: string }
  | { status: 'read_error'; entity: M2cEntityRef; reason: string; observedAt: string }

export type M2cDispatchOutcome =
  | { status: 'confirmed'; entity: M2cEntityRef; acknowledgedAt: string }
  | { status: 'rejected_pre_dispatch'; reason: string }
  | { status: 'rejected'; reason: string; pilotStopped: true }
  | { status: 'unknown'; reason: string; pilotStopped: true }

export const M2C_ACTION_STATES = [
  'proposed', 'blocked', 'cancelled', 'reserved', 'dispatching', 'ack_received', 'reconcile_required',
  'readback_verified', 'observing', 'evaluation_due', 'evaluated_success', 'evaluated_failure',
  'insufficient_data', 'contaminated', 'restore_requested', 'restoring', 'restored', 'restore_unresolved',
] as const
export type M2cActionState = typeof M2C_ACTION_STATES[number]

export interface M2cActionResult {
  action: M2cBidAction
  state: M2cActionState
  outcome: M2cDispatchOutcome | null
  readEvidence: M2cReadEvidence | null
}

export interface M2cPreviewRequest { permitId: string; expectedRevision: number }
/** blockers: global, sperren den ganzen Lauf; blockedCandidates: nur der jeweilige Kandidat ist nicht ausführbar (N1). */
export interface M2cPreviewResponse {
  permit: M2cPilotPermit; actions: M2cBidAction[]; limits: M2cLimits; blockers: string[]
  blockedCandidates: Array<{ entity: M2cEntityRef; reasons: string[] }>
}
export interface M2cRunRequest {
  permitId: string
  expectedRevision: number
  actions: Array<{ actionId: string; expectedRevision: number }>
  requestKey: string
}
export interface M2cRunResponse { runId: string; permitId: string; pilotStatus: 'active' | 'stopped'; results: M2cActionResult[] }
export interface M2cStopRequest { permitId: string; expectedRevision: number; reason: string }
export interface M2cStopResponse { permitId: string; revision: number; status: 'stopped' }
export interface M2cHistoryResponse { actions: M2cActionResult[]; total: number }
export type M2cReconcileResolution = 'readback' | 'accept_not_applied' | 'close_superseded' | 'operator_asserted'
/** Resolutions the server accepts per direction (reconcile.ts STATE_AFTER / RESTORE_STATE_AFTER,
 * bound in tests/m2c/server-contract.test.ts). A restore is never closed as superseded. */
export const M2C_DECREASE_RESOLUTIONS: readonly M2cReconcileResolution[] = ['readback', 'accept_not_applied', 'close_superseded', 'operator_asserted']
export const M2C_RESTORE_RESOLUTIONS: readonly M2cReconcileResolution[] = ['readback', 'accept_not_applied', 'operator_asserted']
export interface M2cReconcileRequest { actionId: string; expectedRevision: number; note: string; resolution: M2cReconcileResolution }
export interface M2cRestoreRequest { actionId: string; expectedRevision: number; ownerApprovalId: string }

/** Pilot wird für diese Zustände nicht fortgesetzt: laufende oder ungeklärte Aktion. */
export const M2C_UNRESOLVED_STATES: ReadonlySet<M2cActionState> = new Set([
  'reserved', 'dispatching', 'ack_received', 'reconcile_required', 'restore_requested', 'restoring', 'restore_unresolved',
])
/** Manuelle Klärung möglich. */
export const M2C_RECONCILE_STATES: ReadonlySet<M2cActionState> = new Set(['reconcile_required', 'restore_unresolved'])
/** Nach bestätigtem Readback: Wiederherstellung kann beantragt werden (Server prüft Freigabe und Frist). */
export const M2C_RESTORABLE_STATES: ReadonlySet<M2cActionState> = new Set([
  'readback_verified', 'observing', 'evaluation_due', 'evaluated_success', 'evaluated_failure', 'insufficient_data', 'contaminated',
])

export const M2C_HISTORY_PAGE_SIZE = 20
const HISTORY_FULL_PAGE = 100
const HISTORY_MAX_PAGES = 10

function parseResponse<T>(schema: z.ZodType<T>, value: unknown): T {
  const parsed = schema.safeParse(value)
  if (!parsed.success) throw new Error('Ungültige M2c-Antwort — Ergebnis unbekannt; Verlauf und Klärung prüfen.')
  return parsed.data
}

/*
 * Server refusal (T4b routes.ts + reply.nack): exactly one error item whose numeric code
 * equals the HTTP status; `field` names the deciding reason. Since T4d run/restore refuse
 * (403/404/409/503) only BEFORE the durable dispatch anchor. After it the executor hull
 * returns only settled or unresolved, which the route answers as 200 or 500
 * M2C_DISPATCH_UNRESOLVED (bound to the server source in tests/m2c/server-contract.test.ts
 * and to the routes in backend tests/m2c/routes.test.ts). The promise covers the hull, not
 * the HTTP layer: a serialization or hook failure after the executor returned reaches the
 * error handler as a generic 500 without field, which latches like any 5xx. So a
 * well-formed refusal envelope whose M2c reason belongs to the status class of routes.ts
 * refuse() proves "nothing sent". 5xx (with or without field), network, parse failure, a
 * malformed envelope, a foreign field or a class mismatch stays Unknown and latches.
 * Reconcile and stop keep their explicit reason lists.
 * Source: backend/src/modules/m2c at T4d (routes, executor, restore, journal, reconcile, policy).
 */
export type M2cWriteOperation = 'run' | 'restore' | 'reconcile' | 'stop'
const ZOD_PATH = /^[a-z][A-Za-z0-9]*(\.[A-Za-z0-9]+)*$/
const M2C_REASON = /^M2C_[A-Z0-9_]+$/
/** The only code of an error after the dispatch anchor (executor.ts); never a refusal. */
const M2C_DISPATCH_UNRESOLVED = 'M2C_DISPATCH_UNRESOLVED'
/** routes.ts refuse(): status of a reason, priority 403 > 404 > 503 > 409. */
const REFUSAL_403 = new Set(['M2C_ACTOR'])
const REFUSAL_404 = new Set(['M2C_PERMIT_NOT_FOUND', 'M2C_ACTION_NOT_FOUND'])
const REFUSAL_503 = new Set(['M2C_STALE_ENTITIES', 'M2C_STALE_METRICS', 'M2C_STALE_SEARCH_TERMS', 'M2C_SYNC_RUNNING',
  'M2C_SYNC_NOT_CLEAN', 'M2C_EVIDENCE_MISSING', 'M2C_LOCAL_BID_UNKNOWN', 'M2C_CONTEXT_UNAVAILABLE', 'M2C_RECONCILE_READ_UNAVAILABLE'])
function refusalStatus(reason: string): number {
  return REFUSAL_403.has(reason) ? 403 : REFUSAL_404.has(reason) ? 404 : REFUSAL_503.has(reason) ? 503 : 409
}
const PRE_DISPATCH_REASONS: Record<'reconcile' | 'stop', ReadonlySet<string>> = {
  // POST /reconcile/ (routes.ts:186-197, reconcile.ts:43-125): sends nothing to Amazon; every
  // refusal is thrown before or inside its single commit, which then rolls back.
  reconcile: new Set(['M2C_ACTION_NOT_FOUND', 'M2C_PERMIT_NOT_FOUND', 'M2C_ACTOR', 'M2C_REVISION_CONFLICT', 'M2C_REVISION_EXHAUSTED',
    'M2C_RECONCILE_RESTORE_UNSUPPORTED', 'M2C_STATE', 'M2C_DISPATCH_MISSING', 'M2C_RECONCILE_ANCHOR', 'M2C_RECONCILE_GRACE',
    'M2C_RECONCILE_READ_UNAVAILABLE', 'M2C_RECONCILE_EVIDENCE']),
  // POST /stop/ (routes.ts:178-184, reconcile.ts stopPilot, journal.ts stopPermit): one journal
  // transaction, no Amazon call.
  stop: new Set(['M2C_PERMIT_NOT_FOUND', 'M2C_ACTOR', 'M2C_REASON', 'M2C_REVISION_CONFLICT', 'M2C_REVISION_EXHAUSTED']),
}
/** preHandler refusals: invalid() 400, requirePermission 403 without field; run/restore/reconcile
 * also requireWriteMode (423 READ_ONLY_MODE, 403 WRITE_ALLOWED_CAMPAIGN_IDS). Stop has no write gate (O1). */
function gateRefusal(operation: M2cWriteOperation, status: number, field: string | undefined): boolean {
  if (status === 400) return field === undefined || ZOD_PATH.test(field)
  if (status === 403 && field === undefined) return true
  if (operation === 'stop') return false
  return (status === 423 && field === 'READ_ONLY_MODE') || (status === 403 && field === 'WRITE_ALLOWED_CAMPAIGN_IDS')
}
const refusalEnvelopeSchema = z.object({
  ack: z.literal(0), warning: z.array(z.unknown()),
  error: z.array(z.object({ code: z.number().int(), message: z.string(), field: z.string().min(1).optional() }).strict()).length(1),
}).strict()
export function isM2cPreDispatchRefusal(err: unknown, operation: M2cWriteOperation): err is ApiError {
  if (!(err instanceof ApiError)) return false
  const parsed = refusalEnvelopeSchema.safeParse(err.body)
  const item = parsed.success ? parsed.data.error[0] : undefined
  if (!item || item.code !== err.status) return false
  if (gateRefusal(operation, err.status, item.field)) return true
  const field = item.field
  if (field === undefined || refusalStatus(field) !== err.status) return false
  if (operation === 'run' || operation === 'restore') return M2C_REASON.test(field) && field !== M2C_DISPATCH_UNRESOLVED
  return PRE_DISPATCH_REASONS[operation].has(field)
}

/** Mirrors the server barrier (policy.unresolved): running/unknown states, or a dispatch
 * outcome Unknown that no server reconciliation closed. Inconsistent DTOs fail closed. */
export function m2cUnresolved(result: M2cActionResult): boolean {
  if (M2C_UNRESOLVED_STATES.has(result.state)) return true
  if (m2cHasPreDispatchProof(result)) return false
  const anchored = result.action.firstDispatchedAt !== null
  switch (result.state) {
    case 'proposed': return result.outcome !== null || result.readEvidence !== null || anchored
    // blocked + Unknown exists only after a server reconciliation (accept_not_applied/operator_asserted).
    // blocked + confirmed only after a reconcile following an ACK (the reconcile keeps the outcome,
    // T6a): closed only with the not-applied read of its direction taken after the ACK (decrease:
    // baseline after the grace; restore: the unchanged decreased bid). Anything else fails closed;
    // the session latch additionally requires its own verified reconcile (m2cHasLatchProof).
    case 'blocked':
      if (anchored && result.outcome?.status === 'confirmed') return !(m2cHasNotAppliedProof(result) || m2cHasRestoreNotAppliedProof(result))
      return !(anchored && (result.outcome?.status === 'rejected' || result.outcome?.status === 'unknown')) &&
        !(!anchored && result.outcome === null && result.readEvidence === null)
    case 'cancelled': return true
    // Verified, assessment, contaminated and restored states carry schema-checked server proof.
    default: return false
  }
}

export function m2cQueryReady(query: { isSuccess: boolean; isError: boolean; isFetching: boolean; isStale: boolean }): boolean {
  return query.isSuccess && !query.isError && !query.isFetching && !query.isStale
}

export async function postM2cPreview(body: M2cPreviewRequest, signal?: AbortSignal) {
  // Vorschau ist serverseitig wirkungsfrei (T4); deshalb abbrechbar wie ein Lesezugriff.
  const response = parseResponse(previewResponseSchema, await api.postLesend<unknown>('/m2c/preview/', body, { signal, requireAckOne: true }))
  if (response.permit.id !== body.permitId || response.permit.revision !== body.expectedRevision) throw new Error('Antwort passt nicht zum angefragten Permit oder zur Revision.')
  return response
}
export async function postM2cRun(body: M2cRunRequest) {
  const response = parseResponse(runResponseSchema, await api.post<unknown>('/m2c/run/', body, { requireAckOne: true }))
  if (response.permitId !== body.permitId || response.results.length !== body.actions.length ||
    response.results.some((r, i) => r.action.id !== body.actions[i]?.actionId || r.action.permitId !== body.permitId ||
      r.action.revision < body.actions[i]!.expectedRevision ||
      (!m2cHasPreDispatchProof(r) && !m2cHasRejectedProof(r) && (!m2cHasResolutionProof(r) || !M2C_RESTORABLE_STATES.has(r.state))) && !M2C_UNRESOLVED_STATES.has(r.state))) {
    throw new Error('Antwort gehört zu einer anderen Aktion oder einem anderen Permit — Ergebnis unbekannt.')
  }
  return response
}
export async function postM2cStop(body: M2cStopRequest) {
  const response = parseResponse(stopResponseSchema, await api.post<unknown>('/m2c/stop/', body, { requireAckOne: true }))
  if (response.permitId !== body.permitId) throw new Error('Stopp-Antwort gehört zu einem anderen Permit — Ergebnis unbekannt.')
  return response
}
export async function getM2cHistory(permitId: string, limit: number, offset: number, signal?: AbortSignal) {
  const query = new URLSearchParams({ permitId, limit: String(limit), offset: String(offset) })
  const response = parseResponse(historyResponseSchema, await api.get<unknown>(`/m2c/history/?${query.toString()}`, { signal, requireAckOne: true }))
  if (response.actions.length > limit || response.actions.length > Math.max(0, response.total - offset) ||
    new Set(response.actions.map(r => r.action.id)).size !== response.actions.length ||
    response.actions.some(r => r.action.permitId !== permitId)) throw new Error('Ungültiger Aktionsverlauf — Änderungen gesperrt.')
  return response
}
async function postActionResult(path: string, body: M2cReconcileRequest | M2cRestoreRequest, permitId: string) {
  const response = parseResponse(actionResultSchema, await api.post<unknown>(path, body, { requireAckOne: true }))
  if (response.action.id !== body.actionId || response.action.permitId !== permitId || response.action.revision < body.expectedRevision ||
    (path === '/m2c/restore/' && response.state !== 'restored' && !M2C_UNRESOLVED_STATES.has(response.state) &&
      !m2cHasPreDispatchProof(response) && !m2cHasRejectedProof(response))) {
    throw new Error('Antwort gehört zu einer anderen Aktion, Revision oder Operation — Ergebnis unbekannt.')
  }
  return response
}
export function postM2cReconcile(body: M2cReconcileRequest, permitId: string) {
  return postActionResult('/m2c/reconcile/', body, permitId)
}
export function postM2cRestore(body: M2cRestoreRequest, permitId: string) {
  return postActionResult('/m2c/restore/', body, permitId)
}

/**
 * Vollständiger Verlauf für die Sperre neuer Läufe und die Klärungsliste.
 * complete=false heißt: ungeklärte Aktionen können nicht ausgeschlossen werden (fail closed).
 */
export async function getM2cFullHistory(permitId: string, signal?: AbortSignal): Promise<{ actions: M2cActionResult[]; total: number; complete: boolean }> {
  const actions: M2cActionResult[] = []
  let total = 0
  for (let page = 0; page < HISTORY_MAX_PAGES; page++) {
    const result = await getM2cHistory(permitId, HISTORY_FULL_PAGE, page * HISTORY_FULL_PAGE, signal)
    if (page > 0 && total !== result.total) throw new Error('Aktionsverlauf während des Ladens geändert — erneut laden.')
    total = result.total
    actions.push(...result.actions)
    if (result.actions.length === 0 || actions.length >= total) break
  }
  return { actions, total, complete: actions.length === total && new Set(actions.map(r => r.action.id)).size === actions.length }
}

export interface M2cViewer { id: number }
export async function getM2cViewer(signal?: AbortSignal): Promise<M2cViewer | null> {
  const result = await api.get<{ user?: { id?: unknown } }>('/auth/me', { signal })
  return typeof result.user?.id === 'number' ? { id: result.user.id } : null
}

export const m2cKeys = {
  all: ['m2c'] as const,
  preview: (permitId: string, revision: number) => ['m2c', 'preview', permitId, revision] as const,
  history: (permitId: string, offset: number) => ['m2c', 'history', permitId, offset] as const,
  fullHistory: (permitId: string) => ['m2c', 'history-full', permitId] as const,
  viewer: ['m2c', 'viewer'] as const,
  safety: (permitId: string) => ['m2c-safety', permitId] as const,
}

export function useM2cPreview(permitId: string, revision: number) {
  return useQuery({
    queryKey: m2cKeys.preview(permitId, revision),
    queryFn: ({ signal }) => postM2cPreview({ permitId, expectedRevision: revision }, signal),
    enabled: permitId.length > 0,
    retry: false,
    staleTime: 30_000,
  })
}
export function useM2cHistory(permitId: string, offset: number) {
  return useQuery({
    queryKey: m2cKeys.history(permitId, offset),
    queryFn: ({ signal }) => getM2cHistory(permitId, M2C_HISTORY_PAGE_SIZE, offset, signal),
    enabled: permitId.length > 0,
    retry: false,
  })
}
export function useM2cFullHistory(permitId: string) {
  return useQuery({
    queryKey: m2cKeys.fullHistory(permitId),
    queryFn: ({ signal }) => getM2cFullHistory(permitId, signal),
    enabled: permitId.length > 0,
    retry: false,
    staleTime: 30_000,
  })
}
export function useM2cViewer() {
  return useQuery({ queryKey: m2cKeys.viewer, queryFn: ({ signal }) => getM2cViewer(signal), retry: false })
}

/**
 * The last unknown operation of one action: direction, the immutable local start of the
 * request (taken before sending, never the time the answer failed), the action revision the
 * request was sent from, and whether a server answer already showed a dispatch anchor.
 * Every server step of that operation bumps the revision (reserve/prepare/transition).
 */
export interface M2cActionLatch { operation: 'decrease' | 'restore'; requestedAt: number; fromRevision: number; dispatched: boolean }
interface PermitSafety {
  latched: boolean; stopUnknown: boolean; busy: boolean; generation: number
  actionIds: string[]; requiredRevisions: Record<string, number>; reconciled: string[]
  operations: Record<string, M2cActionLatch>
  stopLatch: { operation: 'stop'; latchedAt: number } | null
}
// Small client/server clock skew allowance, not a dispatch or durable-operation anchor.
// 120 s tolerates ordinary clock skew but excludes older sessions/days of evidence.
export const M2C_EVIDENCE_CLOCK_SKEW_MS = 120_000
/**
 * Server proof bound to the latched operation (direction + revision it was sent from +
 * request start). Only a server-recorded terminal result that excludes a later execution
 * of that request releases it; elapsed time never does.
 * Restore: a newer revision with a fresh restored baseline (approval consumed, restore
 * dispatch recorded: a still waiting request fails M2C_REPLAY) or an Amazon rejection of
 * that dispatch. An unchanged revision proves nothing at any age: the original request can
 * still wait for its intent (the intent TTL starts only after the lock) and restore later.
 * A restore is never closed by decrease evidence. A verified restore reconcile
 * accept_not_applied (blocked, fresh read of the unchanged decreased bid) releases it only
 * when this session recorded that system reconcile (`systemReconciled`): the DTO cannot tell
 * it from an operator assertion, which never releases.
 * Decrease: a newer revision with fresh target, server-closed reconcile, rejection, or a
 * pre-dispatch cancellation when no server answer ever showed a dispatch anchor.
 */
export function m2cHasLatchProof(result: M2cActionResult, latch: M2cActionLatch | undefined, systemReconciled = false): boolean {
  if (!latch || result.action.revision <= latch.fromRevision) return false
  const fresh = result.readEvidence !== null && Date.parse(result.readEvidence.observedAt) > latch.requestedAt - M2C_EVIDENCE_CLOCK_SKEW_MS
  if (latch.operation === 'restore') {
    return (result.state === 'restored' && fresh && m2cHasResolutionProof(result)) || m2cHasRejectedProof(result) ||
      (systemReconciled && fresh && m2cHasRestoreNotAppliedProof(result))
  }
  if (m2cHasPreDispatchProof(result)) return !latch.dispatched
  if (m2cHasRejectedProof(result)) return true
  return fresh && ((M2C_RESTORABLE_STATES.has(result.state) && m2cHasResolutionProof(result)) ||
    m2cHasNotAppliedProof(result) || m2cHasSupersededProof(result))
}
function actionResults(response: M2cRunResponse | M2cActionResult): M2cActionResult[] {
  return 'results' in response ? response.results : [response]
}
function requestedActions(body: M2cRunRequest | M2cRestoreRequest | M2cReconcileRequest) {
  return 'actions' in body ? body.actions : [body]
}
const INITIAL_SAFETY: PermitSafety = { latched: false, stopUnknown: false, busy: false, generation: 0, actionIds: [], requiredRevisions: {}, reconciled: [], operations: {}, stopLatch: null }
export function useM2cPermitSafety(permitId: string) {
  return useQuery({ queryKey: m2cKeys.safety(permitId), queryFn: () => INITIAL_SAFETY, initialData: INITIAL_SAFETY, enabled: false, staleTime: Infinity, gcTime: Infinity }).data
}

/**
 * Fresh complete history decides; ordinary refetches never call this. The automatic path
 * (after a verified reconcile) needs a recorded system reconciliation per action; the
 * operator's explicit recheck accepts any server proof bound to the latched operation,
 * e.g. when the server closed the action itself or the history caught up later.
 */
async function recheckM2cLatch(client: ReturnType<typeof useQueryClient>, permitId: string, explicit: boolean): Promise<boolean> {
  const key = m2cKeys.safety(permitId)
  const before = client.getQueryData<PermitSafety>(key) ?? INITIAL_SAFETY
  const history = await getM2cFullHistory(permitId)
  client.setQueryData(m2cKeys.fullHistory(permitId), history)
  const current = client.getQueryData<PermitSafety>(key) ?? INITIAL_SAFETY
  if (!current.latched || current.stopUnknown || current.generation !== before.generation || !history.complete ||
    history.actions.some(m2cUnresolved) || !current.actionIds.length ||
    !current.actionIds.every(id => (explicit || current.reconciled.includes(id)) && history.actions.some(r => r.action.id === id &&
      r.action.revision >= (current.requiredRevisions[id] ?? 0) && m2cHasLatchProof(r, current.operations[id], current.reconciled.includes(id))))) return false
  client.setQueryData(key, { ...current, latched: false, actionIds: [], requiredRevisions: {}, reconciled: [], operations: {} })
  return true
}

/** Explicit GET-only recheck; never submits a write. */
export function useM2cRecheckHistory(permitId: string) {
  const client = useQueryClient()
  const key = m2cKeys.safety(permitId)
  return useMutation({ retry: false, mutationFn: async () => {
    const before = client.getQueryData<PermitSafety>(key) ?? INITIAL_SAFETY
    if (before.busy) throw new Error('Klärung läuft bereits.')
    client.setQueryData(key, { ...before, busy: true })
    try { return await recheckM2cLatch(client, permitId, true) }
    finally {
      const current = client.getQueryData<PermitSafety>(key) ?? INITIAL_SAFETY
      client.setQueryData(key, { ...current, busy: false })
    }
  } })
}

// QueryClient owns one latch per Permit, independent of mounted panels and revision.
function usePermitMutation<TBody extends M2cRunRequest | M2cRestoreRequest | M2cReconcileRequest, TResult extends M2cRunResponse | M2cActionResult>(permitId: string, kind: 'run' | 'restore' | 'reconcile',
  fn: (body: TBody) => Promise<TResult>, ids: (body: TBody) => string[],
  mayResolve: (body: TBody) => boolean = () => false) {
  const client = useQueryClient()
  const key = m2cKeys.safety(permitId)
  const read = () => client.getQueryData<PermitSafety>(key) ?? INITIAL_SAFETY
  return useMutation({ retry: false, mutationFn: async (body: TBody) => {
    const before = read()
    if (before.busy || (kind !== 'reconcile' && before.latched)) throw new Error('Permit gesperrt — Klärung erforderlich.')
    // Immutable request start, taken before sending: every later proof is bound to it.
    const requestedAt = Date.now()
    client.setQueryData(key, { ...before, busy: true })
    const rememberRevisions = (results: M2cActionResult[] = []) => {
      const current = read()
      const revisions = { ...current.requiredRevisions }
      const requested = requestedActions(body)
      requested.forEach(a => { revisions[a.actionId] = Math.max(revisions[a.actionId] ?? 0, a.expectedRevision) })
      results.forEach(r => { revisions[r.action.id] = Math.max(revisions[r.action.id] ?? 0, r.action.revision) })
      // A shown dispatch anchor of a latched decrease excludes later "never sent" proof.
      const operations = { ...current.operations }
      results.forEach(r => {
        const op = operations[r.action.id]
        if (op?.operation === 'decrease' && r.action.firstDispatchedAt !== null) operations[r.action.id] = { ...op, dispatched: true }
      })
      client.setQueryData(key, { ...current, requiredRevisions: revisions, operations })
    }
    const latch = (results: M2cActionResult[] = []) => {
      rememberRevisions()
      const current = read()
      const operations = { ...current.operations }
      const requested = requestedActions(body)
      ids(body).forEach(id => {
        // A reconcile (including failure) must never replace the original write's clock/type.
        if (kind === 'reconcile' && operations[id]) return
        const answered = results.find(r => r.action.id === id)
        const known = answered ?? client.getQueryData<{ actions: M2cActionResult[] }>(m2cKeys.fullHistory(permitId))?.actions.find(r => r.action.id === id)
        operations[id] = {
          operation: kind === 'restore' || (kind === 'reconcile' && known && ['restore_requested', 'restoring', 'restore_unresolved', 'restored'].includes(known.state)) ? 'restore' : 'decrease',
          requestedAt,
          // Run/restore: the revision this request was sent from. A reconcile is no new write:
          // its latch guards the earlier operation (sent before this revision), while
          // requiredRevisions still demands at least the reconcile's expected revision.
          fromRevision: kind === 'reconcile' ? 0 : requested.find(a => a.actionId === id)?.expectedRevision ?? 0,
          // Unknown answer of a reconcile: assume dispatched (no pre-dispatch release).
          dispatched: kind === 'reconcile' ? known?.action.firstDispatchedAt !== null : !!answered?.action.firstDispatchedAt,
        }
      })
      client.setQueryData(key, { ...current, latched: true, generation: current.generation + 1, operations,
        actionIds: [...new Set([...current.actionIds, ...ids(body)])], reconciled: current.reconciled.filter(id => !ids(body).includes(id)) })
    }
    let systemProofRecorded = false
    try {
      const response = await fn(body)
      const results = actionResults(response)
      rememberRevisions(results)
      if (kind === 'reconcile' && !mayResolve(body)) {
        // A newer operator assertion cannot inherit earlier system proof.
        client.setQueryData(key, { ...read(), reconciled: read().reconciled.filter(id => !ids(body).includes(id)) })
      }
      // Only a system resolution (never operator_asserted) can carry restore not-applied proof.
      const verified = kind === 'reconcile' && mayResolve(body)
      if (results.some(r => m2cUnresolved(r) || r.outcome?.status === 'rejected' ||
        (kind === 'reconcile' && (!(m2cHasReconcileResolutionProof(r) || (verified && m2cHasRestoreNotAppliedProof(r))) ||
          (read().operations[r.action.id] && !m2cHasLatchProof(r, read().operations[r.action.id], verified)))))) latch(results)
      else if (verified && before.latched &&
        results.every(r => m2cHasLatchProof(r, read().operations[r.action.id], true))) {
        client.setQueryData(key, { ...read(), reconciled: [...new Set([...read().reconciled, ...ids(body)])] })
        systemProofRecorded = true
        // Fresh complete history AFTER validated reconciliation; ordinary refetch never unlocks.
        await recheckM2cLatch(client, permitId, false)
      }
      return response
    } catch (err) {
      if (!systemProofRecorded && !isM2cPreDispatchRefusal(err, kind)) latch()
      throw err
    } finally { client.setQueryData(key, { ...read(), busy: false }) }
  }, onSettled: () => client.invalidateQueries({ queryKey: m2cKeys.all }) })
}
export const useM2cRun = (permitId: string) => usePermitMutation(permitId, 'run', postM2cRun, body => body.actions.map(a => a.actionId))
export function useM2cStop(permitId: string) {
  const client = useQueryClient()
  const key = m2cKeys.safety(permitId)
  const read = () => client.getQueryData<PermitSafety>(key) ?? INITIAL_SAFETY
  // Stop remains available with a latched/busy Permit; it never resolves actions.
  return useMutation({ retry: false, mutationFn: async (body: M2cStopRequest) => {
    try {
      const response = await postM2cStop(body)
      const current = read()
      client.setQueryData(key, { ...current, stopUnknown: false, stopLatch: null,
        latched: current.stopUnknown ? current.actionIds.length > 0 : current.latched })
      return response
    } catch (err) {
      if (!isM2cPreDispatchRefusal(err, 'stop')) client.setQueryData(key, { ...read(), latched: true, stopUnknown: true,
        stopLatch: { operation: 'stop', latchedAt: Date.now() }, generation: read().generation + 1 })
      throw err
    }
  }, onSettled: () => client.invalidateQueries({ queryKey: m2cKeys.all }) })
}
export const useM2cReconcile = (permitId: string) => usePermitMutation(permitId, 'reconcile', (body: M2cReconcileRequest) => postM2cReconcile(body, permitId), body => [body.actionId], body => body.resolution !== 'operator_asserted')
export const useM2cRestore = (permitId: string) => usePermitMutation(permitId, 'restore', (body: M2cRestoreRequest) => postM2cRestore(body, permitId), body => [body.actionId])

export function newM2cRequestKey(): string {
  const random = globalThis.crypto?.randomUUID?.()
  return random ?? `m2c-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
